// =============================================================================
// FILE: packages/renderer/src/lib/civilEstimatorGenerator.ts
// PURPOSE: "Civil Ductbank & Manhole Estimator" + auto-detailing. Computes an
//   underground/bridge/indoor route from coordinates, auto-places manholes,
//   splits the Bill of Materials into Indoor vs Outdoor (bridge as its own
//   section), and renders a multi-page package: route profile, below-grade
//   manhole detail (with keynotes + general notes), and the split BOM.
// =============================================================================

import { jsPDF } from 'jspdf';

const PAGE_W = 1754;
const PAGE_H = 1240;
const MANHOLE_SPACING_FT = 400;

export type SegKind = 'underground' | 'bridge' | 'indoor';
export interface RouteSegment { id: string; name: string; kind: SegKind; origin: string; destination: string }

interface Coord { lat: number; lng: number }
const KNOWN: Record<string, Coord> = {
  calgary: { lat: 51.0447, lng: -114.0719 }, toronto: { lat: 43.6532, lng: -79.3832 },
  vancouver: { lat: 49.2827, lng: -123.1207 }, edmonton: { lat: 53.5461, lng: -113.4938 },
};
function hash(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0) / 4294967295; }
function geocode(input: string): Coord {
  const t = input.trim().toLowerCase();
  const m = t.match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
  if (m) return { lat: parseFloat(m[1]!), lng: parseFloat(m[2]!) };
  for (const k of Object.keys(KNOWN)) if (t.includes(k)) { const b = KNOWN[k]!; return { lat: b.lat + (hash(t) - 0.5) * 0.02, lng: b.lng + (hash(t + 'x') - 0.5) * 0.02 }; }
  const b = KNOWN['calgary']!; return { lat: b.lat + (hash(t) - 0.5) * 0.02, lng: b.lng + (hash('_' + t) - 0.5) * 0.02 };
}
function haversineFt(a: Coord, b: Coord): number {
  const R = 6371000, toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s)) * 3.28084;
}

// Unit rates ($/ft or $/ea)
const RATE = {
  ductbank:  85,   // concrete-encased 4×4" underground, per ft
  bridge:    120,  // aerial / bridge attachment, per ft
  indoorEmt: 28,   // indoor EMT conduit, per ft
  manhole:   9500, // precast 4'×6'×7' manhole, each
};
const PATH_SLACK = 0.10;

export interface BomLine { section: 'Outdoor' | 'Bridge' | 'Indoor'; item: string; qty: number; unit: string; rate: number; total: number }

export interface CivilResult {
  segments: Array<RouteSegment & { lengthFt: number }>;
  outdoorFt: number; bridgeFt: number; indoorFt: number; totalFt: number;
  manholes: number;
  bom: BomLine[];
  materials: number; labor: number; markup: number; total: number;
}

export function computeCivil(segments: RouteSegment[], laborMult: number, markupPct: number): CivilResult {
  const withLen = segments.map((s) => ({ ...s, lengthFt: haversineFt(geocode(s.origin), geocode(s.destination)) * (1 + PATH_SLACK) }));
  const outdoorFt = withLen.filter((s) => s.kind === 'underground').reduce((a, s) => a + s.lengthFt, 0);
  const bridgeFt  = withLen.filter((s) => s.kind === 'bridge').reduce((a, s) => a + s.lengthFt, 0);
  const indoorFt  = withLen.filter((s) => s.kind === 'indoor').reduce((a, s) => a + s.lengthFt, 0);
  const totalFt = outdoorFt + bridgeFt + indoorFt;
  const manholes = outdoorFt > 0 ? Math.ceil(outdoorFt / MANHOLE_SPACING_FT) + 1 : 0;

  const bom: BomLine[] = [];
  const push = (section: BomLine['section'], item: string, qty: number, unit: string, rate: number) =>
    bom.push({ section, item, qty: Math.round(qty), unit, rate, total: qty * rate });
  if (outdoorFt > 0) {
    push('Outdoor', 'Concrete-encased ductbank (4×4" PVC)', outdoorFt, 'ft', RATE.ductbank);
    push('Outdoor', 'Precast manhole 4\u2032×6\u2032×7\u2032', manholes, 'ea', RATE.manhole);
    push('Outdoor', 'OSP fiber (armored, outdoor rated)', outdoorFt, 'ft', 2.4);
  }
  if (bridgeFt > 0) {
    push('Bridge', 'Bridge cable tray / aerial support', bridgeFt, 'ft', RATE.bridge);
    push('Bridge', 'Expansion / seismic fittings', Math.max(2, Math.ceil(bridgeFt / 200)), 'ea', 480);
  }
  if (indoorFt > 0) {
    push('Indoor', 'Indoor EMT conduit', indoorFt, 'ft', RATE.indoorEmt);
    push('Indoor', 'Riser-rated indoor fiber', indoorFt, 'ft', 1.85);
    push('Indoor', 'Fire-stop sleeves', Math.max(2, Math.ceil(indoorFt / 100)), 'ea', 240);
  }
  const materials = bom.reduce((a, l) => a + l.total, 0);
  const labor = materials * 0.45 * laborMult;
  const sub = materials + labor;
  const markup = sub * (markupPct / 100);
  return { segments: withLen, outdoorFt, bridgeFt, indoorFt, totalFt, manholes, bom, materials, labor, markup, total: sub + markup };
}

function esc(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
const usd = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

function frame(inner: string, footer: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}">
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="#0a0f1a"/>
    <rect x="30" y="30" width="${PAGE_W - 60}" height="${PAGE_H - 60}" rx="14" fill="none" stroke="rgba(37,181,168,0.3)" stroke-width="2"/>
    ${inner}
    <text x="70" y="${PAGE_H - 55}" font-family="Inter" font-size="18" fill="rgba(148,172,210,0.6)" letter-spacing="1.5">${footer}</text>
    <g transform="translate(${PAGE_W - 130},${PAGE_H - 120})"><circle r="70" fill="none" stroke="#25B5A8" stroke-width="2" opacity="0.5"/><text y="6" text-anchor="middle" font-family="Inter" font-weight="800" font-size="15" fill="#5EEAD4">CERTIFIED</text><text y="26" text-anchor="middle" font-family="Inter" font-size="11" fill="rgba(148,172,210,0.8)">BY RCD²</text></g>
  </svg>`;
}

export function buildProfileSvg(r: CivilResult, project: string): string {
  const y0 = 420, x0 = 120, x1 = PAGE_W - 160;
  const totalLen = r.totalFt || 1;
  let cx = x0;
  const colorFor: Record<SegKind, string> = { underground: '#F59E0B', bridge: '#F43F5E', indoor: '#38BDF8' };
  const segs = r.segments.map((s) => {
    const w = ((x1 - x0) * s.lengthFt) / totalLen;
    const g = `<rect x="${cx}" y="${y0}" width="${Math.max(4, w)}" height="60" fill="${colorFor[s.kind]}33" stroke="${colorFor[s.kind]}" stroke-width="1.5"/>
      <text x="${cx + 6}" y="${y0 + 36}" font-family="Inter" font-size="15" fill="#E2E8F0">${esc(s.name)}</text>
      <text x="${cx + 6}" y="${y0 + 90}" font-family="Inter" font-size="14" fill="rgba(160,180,215,0.7)">${Math.round(s.lengthFt).toLocaleString()} ft · ${s.kind}</text>`;
    cx += Math.max(4, w);
    return g;
  }).join('');
  // manhole markers along outdoor portion
  const mh = Array.from({ length: r.manholes }, (_, i) => {
    const mx = x0 + ((x1 - x0) * (i / Math.max(1, r.manholes - 1))) * (r.outdoorFt / totalLen);
    return `<circle cx="${mx}" cy="${y0 - 20}" r="9" fill="#0a0f1a" stroke="#F59E0B" stroke-width="2"/><text x="${mx}" y="${y0 - 34}" text-anchor="middle" font-family="Inter" font-size="12" fill="#F59E0B">MH${i + 1}</text>`;
  }).join('');
  const inner = `
    <text x="120" y="90" font-family="Inter" font-weight="800" font-size="40" fill="#F0F5FF">Civil Route Profile</text>
    <text x="122" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">${esc(project)} · DUCTBANK &amp; MANHOLE ROUTE</text>
    <g font-family="Inter">
      ${[['Total route', `${Math.round(r.totalFt).toLocaleString()} ft`], ['Underground', `${Math.round(r.outdoorFt).toLocaleString()} ft`], ['Bridge', `${Math.round(r.bridgeFt).toLocaleString()} ft`], ['Indoor', `${Math.round(r.indoorFt).toLocaleString()} ft`], ['Manholes', String(r.manholes)]]
        .map(([k, v], i) => `<text x="${120 + i * 320}" y="230" font-size="16" fill="rgba(148,172,210,0.7)">${k}</text><text x="${120 + i * 320}" y="268" font-size="30" font-weight="800" fill="#5EEAD4">${v}</text>`).join('')}
    </g>
    ${mh}${segs}
    <text x="120" y="${y0 + 170}" font-family="Inter" font-size="16" fill="rgba(160,180,215,0.7)">Legend: <tspan fill="#F59E0B">■ Underground</tspan>  <tspan fill="#F43F5E">■ Bridge</tspan>  <tspan fill="#38BDF8">■ Indoor</tspan>  · +10% path slack applied · manholes @ ${MANHOLE_SPACING_FT} ft o.c.</text>`;
  return frame(inner, 'CIVIL INFRASTRUCTURE PACKAGE · SHEET 1 / 3 · ROUTE PROFILE');
}

export function buildManholeDetailSvg(project: string): string {
  // plan + section of a precast manhole with keynotes & general notes
  const px = 160, py = 220, pw = 520, ph = 340;
  const keynotes = [
    'Precast concrete manhole, 4\u2032×6\u2032×7\u2032 clear inside.',
    '32" dia. cast-iron frame & cover, H-20 traffic rated.',
    'Cable racking, both long walls (galv. steel).',
    'Pulling irons, each end wall.',
    'Ground rod & bonding to racking + frame.',
    'Sump, 12"×12"×6" with grate.',
    'Ductbank window, grouted watertight.',
  ];
  const notes = [
    'Set manhole on 12" compacted gravel base.',
    'Backfill in 8" lifts, compact to 95% SPD.',
    'All below-grade per ANSI/TIA-758 OSP standards.',
    'Coordinate final grade with civil drawings.',
  ];
  const kn = keynotes.map((t, i) => `<text x="740" y="${240 + i * 40}" font-family="Inter" font-size="17" fill="#E2E8F0"><tspan font-weight="800" fill="#5EEAD4">[${i + 1}]</tspan> ${esc(t)}</text>`).join('');
  const nt = notes.map((t, i) => `<text x="740" y="${600 + i * 34}" font-family="Inter" font-size="16" fill="rgba(160,180,215,0.75)">• ${esc(t)}</text>`).join('');
  const inner = `
    <text x="120" y="90" font-family="Inter" font-weight="800" font-size="40" fill="#F0F5FF">Below-Grade Manhole Detail</text>
    <text x="122" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">${esc(project)} · TYPICAL MH · NOT TO SCALE</text>

    <!-- SECTION view -->
    <text x="${px}" y="${py - 16}" font-family="Inter" font-size="18" font-weight="700" fill="rgba(148,172,210,0.8)">SECTION A-A</text>
    <rect x="${px}" y="${py}" width="${pw}" height="${ph}" fill="rgba(37,181,168,0.05)" stroke="#5EEAD4" stroke-width="2"/>
    <rect x="${px}" y="${py}" width="${pw}" height="24" fill="rgba(100,116,139,0.3)"/>
    <rect x="${px + pw / 2 - 40}" y="${py - 24}" width="80" height="24" fill="none" stroke="#94a3b8" stroke-width="2"/>
    <text x="${px + pw / 2}" y="${py - 30}" text-anchor="middle" font-family="Inter" font-size="13" fill="#94a3b8">FRAME &amp; COVER [2]</text>
    <line x1="${px}" y1="${py + ph - 40}" x2="${px + pw}" y2="${py + ph - 40}" stroke="#64748B" stroke-width="1.5" stroke-dasharray="6 4"/>
    <text x="${px + 12}" y="${py + ph - 48}" font-family="Inter" font-size="13" fill="#94a3b8">CABLE RACKING [3]</text>
    <rect x="${px + 20}" y="${py + ph - 30}" width="60" height="20" fill="#818CF833" stroke="#818CF8"/>
    <text x="${px + 90}" y="${py + ph - 15}" font-family="Inter" font-size="12" fill="#818CF8">DUCTBANK WINDOW [7]</text>
    <text x="${px + pw - 70}" y="${py + 60}" font-family="Inter" font-size="12" fill="#5EEAD4">[1] PRECAST</text>

    <!-- PLAN view -->
    <text x="${px}" y="${py + ph + 60}" font-family="Inter" font-size="18" font-weight="700" fill="rgba(148,172,210,0.8)">PLAN</text>
    <rect x="${px}" y="${py + ph + 76}" width="${pw}" height="200" fill="rgba(37,181,168,0.05)" stroke="#5EEAD4" stroke-width="2"/>
    <circle cx="${px + pw / 2}" cy="${py + ph + 176}" r="26" fill="none" stroke="#94a3b8" stroke-width="2"/>
    <rect x="${px + 10}" y="${py + ph + 150}" width="14" height="52" fill="#F59E0B33" stroke="#F59E0B"/>
    <rect x="${px + pw - 24}" y="${py + ph + 150}" width="14" height="52" fill="#F59E0B33" stroke="#F59E0B"/>
    <text x="${px + 30}" y="${py + ph + 130}" font-family="Inter" font-size="12" fill="#F59E0B">PULLING IRONS [4]</text>

    <text x="740" y="200" font-family="Inter" font-size="22" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.8)">KEYNOTES</text>
    ${kn}
    <text x="740" y="570" font-family="Inter" font-size="22" font-weight="700" letter-spacing="2" fill="rgba(148,172,210,0.8)">GENERAL NOTES</text>
    ${nt}`;
  return frame(inner, 'CIVIL INFRASTRUCTURE PACKAGE · SHEET 2 / 3 · MANHOLE DETAIL');
}

function bomSvg(r: CivilResult, project: string): string {
  const sections: Array<BomLine['section']> = ['Outdoor', 'Bridge', 'Indoor'];
  let y = 220;
  const blocks = sections.map((sec) => {
    const rows = r.bom.filter((b) => b.section === sec);
    if (rows.length === 0) return '';
    const secTotal = rows.reduce((a, b) => a + b.total, 0);
    let out = `<text x="120" y="${y}" font-family="Inter" font-size="22" font-weight="800" fill="#5EEAD4">${sec.toUpperCase()} ITEMS</text>`;
    y += 36;
    for (const b of rows) {
      out += `<text x="140" y="${y}" font-family="Inter" font-size="18" fill="#E2E8F0">${esc(b.item)}</text>
        <text x="1120" y="${y}" font-family="Inter" font-size="18" fill="rgba(200,214,235,0.85)">${b.qty.toLocaleString()} ${b.unit}</text>
        <text x="1320" y="${y}" font-family="Inter" font-size="18" fill="rgba(200,214,235,0.85)">@ ${usd(b.rate)}</text>
        <text x="1580" y="${y}" text-anchor="end" font-family="Inter" font-size="18" font-weight="600" fill="#F0F5FF">${usd(b.total)}</text>`;
      y += 34;
    }
    out += `<text x="1580" y="${y + 4}" text-anchor="end" font-family="Inter" font-size="18" font-weight="800" fill="#5EEAD4">${sec} subtotal: ${usd(secTotal)}</text>`;
    y += 60;
    return out;
  }).join('');
  const inner = `
    <text x="120" y="90" font-family="Inter" font-weight="800" font-size="40" fill="#F0F5FF">Bill of Materials</text>
    <text x="122" y="122" font-family="Inter" font-size="18" fill="#5EEAD4" letter-spacing="3">${esc(project)} · INDOOR / OUTDOOR SPLIT</text>
    ${blocks}
    <rect x="120" y="${Math.min(y, PAGE_H - 220)}" width="1500" height="1.5" fill="rgba(37,181,168,0.3)"/>
    <text x="120" y="${Math.min(y, PAGE_H - 220) + 46}" font-family="Inter" font-size="20" fill="rgba(200,214,235,0.85)">Materials ${usd(r.materials)} · Labor ${usd(r.labor)} · Markup ${usd(r.markup)}</text>
    <text x="1580" y="${Math.min(y, PAGE_H - 220) + 46}" text-anchor="end" font-family="Inter" font-size="30" font-weight="800" fill="#5EEAD4">TOTAL ${usd(r.total)}</text>`;
  return frame(inner, 'CIVIL INFRASTRUCTURE PACKAGE · SHEET 3 / 3 · BOM');
}

function rasterize(svg: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { const c = document.createElement('canvas'); c.width = PAGE_W; c.height = PAGE_H; const x = c.getContext('2d')!; x.fillStyle = '#0a0f1a'; x.fillRect(0, 0, PAGE_W, PAGE_H); x.drawImage(img, 0, 0, PAGE_W, PAGE_H); URL.revokeObjectURL(url); resolve(c.toDataURL('image/png')); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export interface CivilPackageResult { blobUrl: string; fileName: string }

export async function generateCivilPackage(r: CivilResult, project: string): Promise<CivilPackageResult> {
  const pages = [buildProfileSvg(r, project), buildManholeDetailSvg(project), bomSvg(r, project)];
  const urls: string[] = [];
  for (const svg of pages) urls.push(await rasterize(svg));
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
  urls.forEach((u, i) => { if (i > 0) pdf.addPage(); pdf.addImage(u, 'PNG', 0, 0, pw, ph, undefined, 'FAST'); });
  const fileName = `RCD2-Civil-${project.replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}.pdf`;
  return { blobUrl: URL.createObjectURL(pdf.output('blob')), fileName };
}
